import {
  CreateCategoryResponse,
  useDeleteCategory,
  useGetPaginateCategory,
} from '../../api-http/categories';
import { DialogModal } from '../../components';
import { AlertBar } from '../../components/AlertBar';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { notifyMsg } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDistance } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import { RenameForm } from './CategoryRenameForm';
import {
  Button,
  LinearProgress,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useConfirm } from 'material-ui-confirm';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { Edit, Trash } from 'react-feather';

interface CategoryTableProp {
  onRefresh: boolean;
}

export const CategoryTable: FC<CategoryTableProp> = (onRefresh) => {
  const confirm = useConfirm();
  const { switchedOrgId } = useAuth();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;

  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<
    CreateCategoryResponse | undefined
  >();
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.layoutEditor]);

  const {
    data: list,
    isLoading,
    error,
    refetch,
  } = useGetPaginateCategory(pagingParams);

  const { error: deleteError, mutate } = useDeleteCategory({
    onSuccess: () => {
      refetch();
      showSuccessSnack(notifyMsg.CATEGORY_DELETE_SUCCESS);
    },
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const handleEdit = (category: CreateCategoryResponse) => {
    setOpen(true);
    setActiveCategory(category);
  };

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: 'Are you sure you want to delete?',
        confirmationText: 'Yes',
      });
      mutate({ id });
    } catch (e) {}
  };

  useEffect(() => {
    refetch();
  }, [open, switchedOrgId, onRefresh]);

  return (
    <>
      {isLoading && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || deleteError?.msg} />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Category Title
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">Updated</StyledTableCell>
              <StyledTableCell align="right">
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!list?.data?.length && (
              <TableRow>
                {' '}
                <TableCell colSpan={4} align="center">
                  {isLoading ? <CircularProgress /> : 'No Records Found '}
                </TableCell>{' '}
              </TableRow>
            )}
            {list?.data.map((category) => (
              <TableRow
                key={category.name}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="category">
                  {category.name}
                </TableCell>
                <TableCell align="right">
                  {displayDistance(category.updatedAt)}
                </TableCell>
                <TableCell align="right">
                  {displayDistance(category.createdAt)}
                </TableCell>

                <TableCell align="right">
                  <Button
                    onClick={() => handleEdit(category)}
                    disabled={isViewOnly}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    onClick={() => handleDelete(category._id)}
                    disabled={isViewOnly}
                  >
                    <Trash size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Rename Category"
        onClose={() => setOpen(false)}
        open={open}
      >
        <RenameForm
          onFinished={() => setOpen(false)}
          category={activeCategory || undefined}
        />
      </DialogModal>
    </>
  );
};

export default CategoryTable;
